import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Space, Button, Dropdown, Modal, Input, Alert } from '@/shared/ui/primitives';
import { DownOutlined } from '@/shared/ui/icons';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import UserTable from '../components/UserTable';
import { useDeleteUser } from '../hooks/useUserMutations';
import { useBulkUserStatus, useUserSuspend } from '@/features/ops/hooks/useOps';
import DynamicFilterBuilder from '@/shared/filtering/DynamicFilterBuilder';
import { ADMIN_FILTER_RESOURCES } from '@/shared/filtering/types';
import { useDynamicAdminList } from '@/shared/filtering/useDynamicAdminList';
import { nationalityApi } from '@/features/nationalities/api/nationality.api';
import { referralApi } from '@/features/referrals/api/referral.api';

const RESOURCE = ADMIN_FILTER_RESOURCES.users;

export default function UserListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [statusModal, setStatusModal] = useState<{
    mode: 'single' | 'bulk';
    target?: any;
    status: 'ACTIVE' | 'SUSPENDED' | 'UNDER_REVIEW';
  } | null>(null);
  const [reason, setReason] = useState('');

  const list = useDynamicAdminList<any>(RESOURCE, { onFiltersChanged: () => setSelectedIds([]) });
  const { data, isLoading } = list.searchQuery as any;
  const deleteMutation = useDeleteUser();
  const suspendMutation = useUserSuspend();
  const bulkMutation = useBulkUserStatus();

  const relationLookups = useMemo(() => ({
    'nationality.id': {
      fetchFn: ({ search, page, size }: any) => nationalityApi.list({ page, size, countryNameEn: search }),
      fetchById: (id: string | number) => nationalityApi.getById(id),
      mapOption: (n: any) => ({ value: n.id, label: n.countryNameEn || n.nameEn || n.countryNameAr || `#${n.id}` }),
      placeholder: t('users.nationalityId'),
    },
    'referral.id': {
      fetchFn: ({ search, page, size }: any) => referralApi.list({ page, size, nameEn: search }),
      fetchById: (id: string | number) => referralApi.getById(id),
      mapOption: (r: any) => ({ value: r.id, label: r.nameEn || r.nameAr || `#${r.id}` }),
      placeholder: t('users.referralId'),
    },
  }), [t]);

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, { onSettled: () => setDeleteTarget(null) });
  };

  const handleStatusChange = (target: any, status: 'ACTIVE' | 'SUSPENDED' | 'UNDER_REVIEW') => {
    setReason('');
    setStatusModal({ mode: 'single', target, status });
  };

  const confirmStatus = () => {
    if (!statusModal) return;
    if (statusModal.mode === 'single') {
      suspendMutation.mutate(
        { id: statusModal.target.id, status: statusModal.status, reason: reason.trim() || undefined },
        { onSettled: () => { setStatusModal(null); setReason(''); } },
      );
    } else {
      bulkMutation.mutate(
        { userIds: selectedIds, status: statusModal.status, reason: reason.trim() || undefined },
        {
          onSettled: () => { setStatusModal(null); setReason(''); setSelectedIds([]); },
        },
      );
    }
  };

  const bulkMenu = {
    items: [
      { key: 'suspend', label: t('userOps.bulk.suspend'), onClick: () => { setReason(''); setStatusModal({ mode: 'bulk', status: 'SUSPENDED' }); } },
      { key: 'review', label: t('userOps.bulk.flag'), onClick: () => { setReason(''); setStatusModal({ mode: 'bulk', status: 'UNDER_REVIEW' }); } },
      { key: 'restore', label: t('userOps.bulk.reactivate'), onClick: () => { setReason(''); setStatusModal({ mode: 'bulk', status: 'ACTIVE' }); } },
    ],
  };

  return (
    <div>
      <PageHeader title={t('users.title')} subtitle={t('users.subtitle')} />
      <DynamicFilterBuilder resource={list.resource} metadata={list.metadataQuery.data}
        loading={list.metadataQuery.isLoading}
        value={list.filters}
        relationLookups={relationLookups}
        onChange={list.setFilters}
        onReset={list.resetFilters}
      />

      {selectedIds.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('userOps.bulk.selected', { count: selectedIds.length })}
          action={
            <Space>
              <Dropdown menu={bulkMenu} trigger={['click']}>
                <Button type="primary">{t('userOps.bulk.action')} <DownOutlined /></Button>
              </Dropdown>
              <Button onClick={() => setSelectedIds([])}>{t('userOps.bulk.clear')}</Button>
            </Space>
          }
        />
      )}

      <div style={{ marginTop: 16 }}>
        <UserTable
          data={data?.content || []}
          loading={isLoading}
          sortField={list.sortField}
          sortDir={list.sortDir}
          onSort={list.handleSort}
          onView={(id) => navigate(`/users/${id}`)}
          onEdit={(id) => navigate(`/users/${id}/edit`)}
          onDelete={(item) => setDeleteTarget(item)}
          onStatusChange={handleStatusChange}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          page={data?.currentPage ?? 0}
          pageSize={list.size}
          total={data?.totalItems ?? 0}
          onPageChange={list.setPage}
        />
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('users.archiveTitle')}
        message={`${t('users.archiveMessage')} "${deleteTarget?.firstName} ${deleteTarget?.lastName}"?`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteMutation.isPending}
      />

      <Modal
        open={!!statusModal}
        onCancel={() => { setStatusModal(null); setReason(''); }}
        onOk={confirmStatus}
        okText={
          statusModal?.status === 'SUSPENDED' ? t('userOps.modal.suspend')
            : statusModal?.status === 'ACTIVE' ? t('userOps.modal.reactivate')
              : t('userOps.modal.flag')
        }
        okButtonProps={{
          danger: statusModal?.status === 'SUSPENDED',
          loading: suspendMutation.isPending || bulkMutation.isPending,
        }}
        title={
          statusModal?.mode === 'bulk'
            ? t('userOps.modal.titleBulk', { status: statusModal ? t(`accountStatus.${statusModal.status}`) : '', count: selectedIds.length })
            : statusModal
              ? t('userOps.modal.titleSingle', {
                  status: t(`accountStatus.${statusModal.status}`),
                  name: `${statusModal.target?.firstName ?? ''} ${statusModal.target?.lastName ?? ''}`.trim(),
                })
              : ''
        }
      >
        <p style={{ marginBottom: 12 }}>
          {statusModal?.status === 'SUSPENDED' && t('userOps.modal.suspendedHint')}
          {statusModal?.status === 'UNDER_REVIEW' && t('userOps.modal.reviewHint')}
          {statusModal?.status === 'ACTIVE' && t('userOps.modal.activeHint')}
        </p>
        <Input.TextArea
          rows={3}
          placeholder={t('userOps.modal.reasonPlaceholder')}
          value={reason}
          onChange={(e: any) => setReason(e.target.value)}
          data-allstak-mask="true"
          data-sensitive="true"
        />
      </Modal>
    </div>
  );
}
